import React, { useState, type ReactNode, type HTMLAttributes } from "react";
import Box from "@mui/material/Box";
import ToolTipComponent from "../tooltip";
import type { TranslatedString } from "@i18n";

interface TextProps extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  children?: ReactNode;
  className?: string;
  /** Tooltip content - strings must be wrapped in t() for internationalization */
  tooltipContent?: TranslatedString | React.ReactElement;
  alwaysShowTooltip?: boolean;
  sx?: any;
  placement?: any;
  grid?: boolean;
  noWrap?: boolean;
}

const sxMapper: any = {
  grid: {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 1,
  },
  normal: {
    display: "inline-block",
    maxWidth: "100%",
    whiteSpace: "nowrap",
  },
  noWrap: {
    display: "inline-block",
    maxWidth: "100%",
    whiteSpace: "normal",
  },
};

const Text: React.FC<TextProps> = ({
  children,
  className,
  tooltipContent,
  alwaysShowTooltip = false,
  style = {},
  sx,
  placement,
  grid = false,
  noWrap = false,
  ...rest
}) => {
  const [tooltipTitle, setTooltipTitle] = useState<TranslatedString | React.ReactElement | null>(null);
  const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
    const el = event.currentTarget;
    const hasOverflow = el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;

    if (alwaysShowTooltip || (hasOverflow && (sx?.whiteSpace != "normal" || noWrap))) {
      setTooltipTitle(tooltipContent || (String(children) as TranslatedString) || null);
    }
  };

  const handleMouseLeave = () => {
    setTooltipTitle(null);
  };

  return (
    <ToolTipComponent content={tooltipTitle} position={placement ?? "top"}>
      <Box
        component="div"
        className={className}
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          verticalAlign: "bottom",
          fontFamily: "inherit",
          ...sxMapper[grid ? "grid" : noWrap ? "noWrap" : "normal"],
          ...sx,
          ...style,
        }}
        {...rest}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </Box>
    </ToolTipComponent>
  );
};

export default Text;
